import { useEffect, useState } from 'react'
import { InteractiveRenderer } from '@aurora/interactives'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import {
  activeChild,
  draftMaterial,
  materialStatus,
  materials,
  suggestedMaterials,
  type Suggestion,
} from '../store/selectors'
import { readSpec } from '../lib/specs'
import { todayISO } from '../lib/dates'
import { useResolvedTheme } from '../hooks/useTheme'
import { MaterialCard } from '../components/studio/MaterialCard'
import { LibraryGrid } from '../components/studio/LibraryGrid'
import { CreateSheet } from '../components/studio/CreateSheet'

/**
 * Studio — everything made for her, and the way to make more.
 *
 * Today's shelf comes first, because the thing a parent is here for at 9am is
 * "what am I running in the next ten minutes", not a catalogue. The full
 * library sits under it for the Sunday-evening visit.
 *
 * Create stays reachable by thumb the whole way down the page, offset from the
 * capture button so the two never fight for the same corner.
 */
export function StudioView() {
  const child = useAuroraStore(activeChild)
  const date = useAuroraStore((s: any) => s.ui.selectedDate ?? todayISO(child?.timezone))
  const suggestions = useAuroraStore((s: any) => suggestedMaterials(s, date))
  const kept = useAuroraStore(materials)
  const draft = useAuroraStore(draftMaterial)
  const [creating, setCreating] = useState(false)
  const [printing, setPrinting] = useState<string | null>(null)

  // A generation started elsewhere — from the thread, or before a reload —
  // reopens here, so the parent never loses track of something being made.
  useEffect(() => {
    if (draft) setCreating(true)
  }, [draft])

  const busy = draft ? materialStatus(draft) === 'generating' : false

  return (
    <div className="page">
      <header className="page-head reveal" style={{ '--i': 0 } as React.CSSProperties}>
        <div className="page-eyebrow">Studio</div>
        <h1 className="page-title">Things to play</h1>
        <p className="page-sub">
          {kept.length > 0
            ? `${kept.length} made for ${child?.name ?? 'her'} so far. Ask for anything else.`
            : `Everything here gets built around ${child?.name ?? 'her'} specifically.`}
        </p>
      </header>

      <section className="section reveal" style={{ '--i': 1 } as React.CSSProperties}>
        <div className="section-head">
          <h2 className="section-title">Suggested for today</h2>
        </div>

        {suggestions.length === 0 ? (
          <div className="rail-empty">
            <p className="rail-empty-body">
              Nothing queued for today yet. Make one thing for a block and it will be waiting here
              tomorrow morning.
            </p>
          </div>
        ) : (
          <ul className="rail">
            {suggestions.map(({ artifact, reason }: Suggestion, i: number) => (
              <li
                key={artifact._id}
                className="rail-item reveal"
                style={{ '--i': i + 2 } as React.CSSProperties}
              >
                <MaterialCard artifact={artifact} reason={reason} onPrint={setPrinting} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="section">
        <div className="section-head">
          <h2 className="section-title">Library</h2>
        </div>
        <LibraryGrid onPrint={setPrinting} />
      </section>

      <div className="studio-dock">
        <button
          className="btn btn-primary studio-create"
          onClick={() => setCreating(true)}
          aria-haspopup="dialog"
        >
          {busy ? <SpinnerIcon /> : <PlusIcon />}
          {busy ? 'Making one…' : 'Make something'}
        </button>
      </div>

      {creating && (
        <CreateSheet
          onClose={() => {
            setCreating(false)
            // Leaving the sheet lets go of the draft pointer; the material
            // itself keeps generating and lands in the library on its own.
            if (!draft || materialStatus(draft) !== 'ready') {
              ;(auroraStore.getState() as any).clearDraftMaterial()
            }
          }}
        />
      )}

      {printing && <PrintSheet artifactId={printing} onDone={() => setPrinting(null)} />}
    </div>
  )
}

/**
 * Printing.
 *
 * The same renderer draws the sheet — there is no second, paper-only version
 * of an activity to drift out of sync with the real one. A print stylesheet
 * hides the app and leaves this on the page, so what comes out of the printer
 * is the activity itself.
 */
function PrintSheet({ artifactId, onDone }: { artifactId: string; onDone: () => void }) {
  const artifact = useAuroraStore((s: any) => s.artifacts[artifactId])
  const theme = useResolvedTheme()
  const read = readSpec(artifact)

  useEffect(() => {
    if (!read.ok) {
      onDone()
      return
    }
    const after = () => onDone()
    window.addEventListener('afterprint', after)
    // One frame for the renderer to lay out before the dialog freezes it.
    const id = window.setTimeout(() => window.print(), 120)
    return () => {
      window.clearTimeout(id)
      window.removeEventListener('afterprint', after)
    }
  }, [read.ok, onDone])

  if (!read.ok) return null

  return (
    <div className="print-sheet">
      <h1 className="print-sheet-title">{read.spec.title}</h1>
      <InteractiveRenderer spec={read.spec} theme={theme === 'dark' ? 'light' : theme} onResult={() => {}} />
    </div>
  )
}

const S = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.1,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const PlusIcon = () => (
  <svg {...S} width="16" height="16">
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const SpinnerIcon = () => (
  <svg {...S} width="16" height="16" className="spin">
    <path d="M12 3a9 9 0 0 1 9 9" />
  </svg>
)
